import React, { useState } from "react";
import { FileText, Shield, Cookie, ArrowLeft } from "lucide-react";

const currentYear = new Date().getFullYear();
const currentMonthIndex = new Date().getMonth();
const monthNamesEn = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const currentMonthEn = monthNamesEn[currentMonthIndex];
const currentMonthVi = `Tháng ${currentMonthIndex + 1}`;

const APP_VERSION = "2.1";
const LAST_UPDATED_EN = `${currentMonthEn} ${currentYear}`;
const LAST_UPDATED_VI = `${currentMonthVi} ${currentYear}`;

interface FooterProps {
  language: string;
}

const Footer: React.FC<FooterProps> = ({ language }) => {
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showCookies, setShowCookies] = useState(false);

  const translations = {
    en: {
      copyrightDesktop: `© 2023 - ${currentYear}. Developed by Tai Nguyen - All rights reserved`,
      copyrightMobile: `© ${currentYear} - Tai Nguyen. All rights reserved`,
      terms: "Terms of Use",
      privacy: "Privacy Policy",
      cookies: "Cookies Policy",
      version: `Version ${APP_VERSION} - Last updated ${LAST_UPDATED_EN}`,
      quickLinks: "Quick Links",
      home: "Home",
      about: "About",
      skills: "Skills",
      projects: "Projects",
      contact: "Contact",
      back: "Back to website",
      termsContent: `1. Website Content
- All content on this website, including text, images, videos, and other materials, is owned by Tai Nguyen or used with permission.
- You may view and share content on this website for personal and non-commercial purposes.
- You may not copy, distribute, modify, or use content on this website for any commercial purpose without written permission from Tai Nguyen.

2. User Behavior
- You agree not to use this website for any unlawful or harmful purpose.
- You agree not to post or transmit any content that is offensive, defamatory, threatening, or violates others' privacy.
- You agree not to use this website to distribute viruses, malware, or other harmful code.

3. Limitation of Liability
- Tai Nguyen is not responsible for any loss or damage arising from the use of this website.
- Tai Nguyen does not guarantee that this website will operate uninterrupted or error-free.
- Content on this website is for reference only and does not constitute professional advice.

4. Links to Other Websites
- This website may contain links to third-party websites.
- Tai Nguyen is not responsible for the content or security of these websites.

5. Changes to Terms
- Tai Nguyen reserves the right to change these terms at any time without prior notice.
- Your continued use of this website after terms are changed implies acceptance of those changes.

6. Applicable Law
- These terms are governed by the laws of Vietnam.

7. Contact
- If you have any questions about these terms, please contact Tai Nguyen via email: [ntainguyen.it@gmail.com]`,
      privacyContent: `1. Information We Collect
- Information you provide: We may collect your personal information when you contact us via email or contact form, such as name, email address, and any other information you choose to provide.
- Automatically collected information: We may automatically collect certain information when you visit our website, such as IP address, browser type, operating system, and other usage data.

2. How We Use Your Information
- We may use your personal information to respond to your requests, provide information or services you request, and improve our website.
- We may use automatically collected information to analyze trends, manage the website, and collect demographic information.

3. Sharing Your Information
- We will not share your personal information with third parties unless required by law or necessary to protect our rights.

4. Protecting Your Information
- We implement reasonable measures to protect your personal information from unauthorized access, use, or disclosure.

5. Links to Other Websites
- Our website may contain links to third-party websites. We are not responsible for the content or privacy policies of these websites.

6. Changes to Privacy Policy
- We reserve the right to change this privacy policy at any time without prior notice. Your continued use of this website after changes are posted implies acceptance of those changes.

7. Contact
- If you have any questions about this privacy policy, please contact us via email: [ntainguyen.it@gmail.com]`,
      cookiesContent: `1. What are Cookies?
- Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They allow the website to recognize your device and remember certain information about your visit.

2. How We Use Cookies
- We use cookies for the following purposes:
  + Essential Cookies: These are necessary for the website to function properly.
  + Analytics Cookies: We use these to understand how visitors interact with our website (e.g., page visits, time spent) to improve user experience.
  + Functional Cookies: These allow the website to remember choices you make (such as your preferred language).

3. Third-Party Cookies
- We may use third-party services (such as Google Analytics or embedded content from YouTube/GitHub) which may set their own cookies on your device. We do not control these cookies.

4. Managing Cookies
- You can choose to disable cookies through your browser settings. However, please note that disabling cookies may affect the functionality of this website.

5. Contact
- If you have any questions about our Cookie Policy, please contact: [ntainguyen.it@gmail.com]`,
    },
    vi: {
      copyrightDesktop: `© 2023 - ${currentYear}. Phát triển bởi Tai Nguyen - Đã đăng ký bản quyền`,
      copyrightMobile: `© ${currentYear} - Tai Nguyen. All rights reserved`,
      terms: "Điều khoản sử dụng",
      privacy: "Chính sách bảo mật",
      cookies: "Chính sách Cookie",
      version: `Phiên bản ${APP_VERSION} - Cập nhật lần cuối ${LAST_UPDATED_VI}`,
      quickLinks: "Liên Kết Nhanh",
      home: "Trang Chủ",
      about: "Giới Thiệu",
      skills: "Kỹ Năng",
      projects: "Dự Án",
      contact: "Liên Hệ",
      back: "Quay lại trang web",
      termsContent: `1. Nội dung trang web
- Tất cả nội dung trên trang web này, bao gồm văn bản, hình ảnh, video và các tài liệu khác, đều thuộc sở hữu của Tai Nguyen hoặc được sử dụng với sự cho phép.
- Bạn có thể xem và chia sẻ nội dung trên trang web này cho mục đích cá nhân và phi thương mại.
- Bạn không được phép sao chép, phân phối, sửa đổi hoặc sử dụng nội dung trên trang web này cho bất kỳ mục đích thương mại nào mà không có sự cho phép bằng văn bản của Tai Nguyen.

2. Hành vi người dùng
- Bạn đồng ý không sử dụng trang web này cho bất kỳ mục đích bất hợp pháp hoặc gây hại nào.
- Bạn đồng ý không đăng tải hoặc truyền tải bất kỳ nội dung nào có tính chất xúc phạm, phỉ báng, đe dọa hoặc vi phạm quyền riêng tư của người khác.
- Bạn đồng ý không sử dụng trang web này để phát tán virus, phần mềm độc hại hoặc các mã độc hại khác.

3. Giới hạn trách nhiệm
- Tai Nguyen không chịu trách nhiệm cho bất kỳ tổn thất hoặc thiệt hại nào phát sinh từ việc sử dụng trang web này.
- Tai Nguyen không đảm bảo rằng trang web này sẽ luôn hoạt động không bị gián đoạn hoặc không có lỗi.
- Nội dung trên trang web này chỉ mang tính chất tham khảo và không cấu thành lời khuyên chuyên môn.

4. Liên kết đến các trang web khác
- Trang web này có thể chứa các liên kết đến các trang web của bên thứ ba.
- Tai Nguyen không chịu trách nhiệm cho nội dung hoặc tính bảo mật của các trang web này.

5. Thay đổi điều khoản
- Tai Nguyen có quyền thay đổi các điều khoản này bất kỳ lúc nào mà không cần thông báo trước.
- Việc bạn tiếp tục sử dụng trang web này sau khi các điều khoản được thay đổi đồng nghĩa với việc bạn chấp nhận các thay đổi đó.

6. Luật áp dụng
- Các điều khoản này được điều chỉnh bởi luật pháp của Việt Nam.

7. Liên hệ
- Nếu bạn có bất kỳ câu hỏi nào về các điều khoản này, vui lòng liên hệ với Tai Nguyen qua gmail: [ntainguyen.it@gmail.com]`,
      privacyContent: `1. Thông tin mình thu thập
- Thông tin bạn cung cấp: Mình có thể thu thập thông tin cá nhân của bạn khi bạn liên hệ với mình qua email hoặc biểu mẫu liên hệ, chẳng hạn như tên, địa chỉ email và bất kỳ thông tin nào khác bạn chọn cung cấp.
- Thông tin tự động thu thập: Mình có thể tự động thu thập một số thông tin nhất định khi bạn truy cập trang web của mình, chẳng hạn như địa chỉ IP, loại trình duyệt, hệ điều hành và các dữ liệu sử dụng khác.

2. Cách mình sử dụng thông tin của bạn
- Mình có thể sử dụng thông tin cá nhân của bạn để trả lời các yêu cầu của bạn, cung cấp thông tin hoặc dịch vụ mà bạn yêu cầu và cải thiện trang web của mình.
- Mình có thể sử dụng thông tin tự động thu thập để phân tích xu hướng, quản lý trang web và thu thập thông tin nhân khẩu học.

3. Chia sẻ thông tin của bạn
- Mình sẽ không chia sẻ thông tin cá nhân của bạn với các bên thứ ba, trừ khi được yêu cầu bởi luật pháp hoặc khi cần thiết để bảo vệ quyền của mình.

4. Bảo mật thông tin của bạn
- Mình thực hiện các biện pháp hợp lý để bảo vệ thông tin cá nhân của bạn khỏi bị truy cập, sử dụng hoặc tiết lộ trái phép.

5. Liên kết đến các trang web khác
- Trang web của mình có thể chứa các liên kết đến các trang web của bên thứ ba. Mình không chịu trách nhiệm về nội dung hoặc chính sách bảo mật của các trang web này.

6. Thay đổi chính sách bảo mật
- Mình có quyền thay đổi chính sách bảo mật này bất kỳ lúc nào mà không cần thông báo trước. Việc bạn tiếp tục sử dụng trang web này sau khi các thay đổi được đăng tải đồng nghĩa với việc bạn chấp nhận các thay đổi đó.

7. Liên hệ
- Nếu bạn có bất kỳ câu hỏi nào về chính sách bảo mật này, vui lòng liên hệ với mình qua gmail: [ntainguyen.it@gmail.com]`,
      cookiesContent: `1. Cookie là gì?
- Cookie là các tệp văn bản nhỏ được lưu trữ trên thiết bị của bạn (máy tính, máy tính bảng hoặc điện thoại) khi bạn truy cập một trang web. Chúng cho phép trang web ghi nhớ thiết bị của bạn và một số thông tin về lượt truy cập của bạn.

2. Cách mình sử dụng Cookie
- Mình sử dụng cookie cho các mục đích sau:
  + Cookie thiết yếu: Cần thiết để trang web hoạt động bình thường.
  + Cookie phân tích: Giúp mình hiểu cách khách truy cập tương tác với trang web (ví dụ: số lượt truy cập, thời gian truy cập) để cải thiện trải nghiệm người dùng.
  + Cookie chức năng: Cho phép trang web ghi nhớ các lựa chọn của bạn (chẳng hạn như ngôn ngữ ưu tiên).

3. Cookie của bên thứ ba
- Mình có thể sử dụng các dịch vụ của bên thứ ba (như Google Analytics hoặc nội dung nhúng từ YouTube/GitHub), các dịch vụ này có thể đặt cookie riêng của họ trên thiết bị của bạn. Mình không kiểm soát các cookie này.

4. Quản lý Cookie
- Bạn có thể chọn tắt cookie thông qua cài đặt trình duyệt của mình. Tuy nhiên, xin lưu ý rằng việc tắt cookie có thể ảnh hưởng đến một số chức năng của trang web này.

5. Liên hệ
- Nếu bạn có bất kỳ câu hỏi nào về Chính sách Cookie, vui lòng liên hệ: [ntainguyen.it@gmail.com]`,
    },
  };

  const t = translations[language as keyof typeof translations];

  const formatContent = (text: string) => {
    return text.split("\n").map((line, index) => {
      if (line.trim() === "") {
        return <div key={index} className="h-2 md:h-4"></div>;
      }

      if (/^\d+\.\s/.test(line.trim())) {
        return (
          <h3
            key={index}
            className="font-bold text-xl md:text-2xl text-black mt-6 mb-3"
          >
            {line}
          </h3>
        );
      }

      return (
        <p
          key={index}
          className="text-gray-700 text-base md:text-lg mb-2 leading-relaxed"
        >
          {line}
        </p>
      );
    });
  };

  const quickLinks = [
    { id: "home", label: t.home },
    { id: "about", label: t.about },
    { id: "skills", label: t.skills },
    { id: "projects", label: t.projects },
    { id: "contact", label: t.contact },
  ];

  const socialLinks = [
    {
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
        </svg>
      ),
      url: "https://mail.google.com/mail/?view=cm&fs=1&to=ntainguyen.it@gmail.com",
      label: "Email",
    },
    {
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
        </svg>
      ),
      url: "https://linkedin.com/in/ntainguyenit",
      label: "LinkedIn",
    },
    {
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
        </svg>
      ),
      url: "https://github.com/ntainguyenit",
      label: "GitHub",
    },
    {
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      ),
      url: "https://t.me/ntainguyenit",
      label: "Telegram",
    },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer className="py-8 px-6 shadow-inner relative z-10">
      <div className="max-w-5xl mx-auto">
        {/* Quick Links */}
        <div className="mb-6">
          <div className="flex flex-wrap justify-center items-center gap-0.5 text-sm px-1 whitespace-nowrap overflow-x-auto">
            {quickLinks.map((link) => (
              <React.Fragment key={link.id}>
                <button
                  onClick={() => scrollToSection(link.id)}
                  className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-300 transition-colors duration-300 font-medium px-0.5 whitespace-nowrap flex-shrink-0"
                >
                  {link.label}
                </button>
                {quickLinks.indexOf(link) < quickLinks.length - 1 && (
                  <span className="text-gray-400 dark:text-gray-500 mx-0.5">
                    •
                  </span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Social links */}
        <div className="flex justify-center space-x-4 mb-6">
          {socialLinks.map((link, index) => (
            <a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 rounded-full bg-gray-100/80 dark:bg-gray-700/80 backdrop-blur-sm text-gray-700 dark:text-gray-300
                        hover:bg-blue-100 dark:hover:bg-blue-900 hover:text-blue-600 dark:hover:text-blue-300
                        hover:scale-110 transition-all duration-300 shadow-md hover:shadow-xl"
              aria-label={link.label}
            >
              {link.icon}
            </a>
          ))}
        </div>

        {/* Terms, Privacy AND Cookies Buttons */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-4 text-sm">
          <button
            onClick={() => setShowTerms(true)}
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-300"
          >
            <FileText size={16} className="mr-1" />
            {t.terms}
          </button>
          <button
            onClick={() => setShowPrivacy(true)}
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-300"
          >
            <Shield size={16} className="mr-1" />
            {t.privacy}
          </button>
          <button
            onClick={() => setShowCookies(true)}
            className="hidden md:flex items-center text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-300"
          >
            <Cookie size={16} className="mr-1" />
            {t.cookies}
          </button>
        </div>

        {/* Copyright and Version */}
        <div className="flex justify-center items-center text-center">
          <p className="hidden md:block text-gray-600 dark:text-gray-300">{t.copyrightDesktop}</p>
          <p className="md:hidden text-gray-600 dark:text-gray-300">{t.copyrightMobile}</p>
          <span className="hidden md:inline mx-2 text-gray-400 dark:text-gray-500">|</span>
          <p className="hidden md:block text-gray-500 dark:text-gray-400 text-sm">
            {t.version}
          </p>
        </div>
      </div>

      {/* Terms Full Page View */}
      {showTerms && (
        <div className="fixed inset-0 z-[9999] bg-gray-50 text-black font-sans overflow-y-auto w-full h-full">
          <div className="max-w-4xl mx-auto px-6 pt-32 pb-16 md:pt-40 md:pb-20">
            <button
              onClick={() => setShowTerms(false)}
              className="flex items-center text-gray-500 hover:text-black transition-colors mb-8 md:mb-10 font-medium"
            >
              <ArrowLeft size={20} className="mr-2" />
              {t.back}
            </button>
            <h1 className="text-3xl md:text-4xl font-bold mb-8 flex items-center">
              <FileText size={36} className="mr-4 text-black" />
              {t.terms}
            </h1>
            <div>{formatContent(t.termsContent)}</div>
          </div>
        </div>
      )}

      {/* Privacy Full Page View */}
      {showPrivacy && (
        <div className="fixed inset-0 z-[9999] bg-gray-50 text-black font-sans overflow-y-auto w-full h-full">
          <div className="max-w-4xl mx-auto px-6 pt-32 pb-16 md:pt-40 md:pb-20">
            <button
              onClick={() => setShowPrivacy(false)}
              className="flex items-center text-gray-500 hover:text-black transition-colors mb-8 md:mb-10 font-medium"
            >
              <ArrowLeft size={20} className="mr-2" />
              {t.back}
            </button>
            <h1 className="text-3xl md:text-4xl font-bold mb-8 flex items-center">
              <Shield size={36} className="mr-4 text-black" />
              {t.privacy}
            </h1>
            <div>{formatContent(t.privacyContent)}</div>
          </div>
        </div>
      )}

      {/* Cookies Full Page View */}
      {showCookies && (
        <div className="fixed inset-0 z-[9999] bg-gray-50 text-black font-sans overflow-y-auto w-full h-full">
          <div className="max-w-4xl mx-auto px-6 pt-32 pb-16 md:pt-40 md:pb-20">
            <button
              onClick={() => setShowCookies(false)}
              className="flex items-center text-gray-500 hover:text-black transition-colors mb-8 md:mb-10 font-medium"
            >
              <ArrowLeft size={20} className="mr-2" />
              {t.back}
            </button>
            <h1 className="text-3xl md:text-4xl font-bold mb-8 flex items-center">
              <Cookie size={36} className="mr-4 text-black" />
              {t.cookies}
            </h1>
            <div>{formatContent(t.cookiesContent)}</div>
          </div>
        </div>
      )}
    </footer>
  );
};

export const TermsContentModal: React.FC<{
  language: string;
  onClose: () => void;
}> = ({ language, onClose }) => {
  // Prevent background scroll
  React.useEffect(() => {
    const originalBodyStyle = document.body.style.overflow;
    const originalHtmlStyle = document.documentElement.style.overflow;
    
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalBodyStyle;
      document.documentElement.style.overflow = originalHtmlStyle;
    };
  }, []);

  const translations = {
    en: {
      back: "Back to website",
      terms: "Terms of Use",
      termsContent: `1. Website Content\n- All content on this website, including text, images, videos, and other materials, is owned by Tai Nguyen or used with permission.\n- You may view and share content on this website for personal and non-commercial purposes.\n- You may not copy, distribute, modify, or use content on this website for any commercial purpose without written permission from Tai Nguyen.\n\n2. User Behavior\n- You agree not to use this website for any unlawful or harmful purpose.\n- You agree not to post or transmit any content that is offensive, defamatory, threatening, or violates others' privacy.\n- You agree not to use this website to distribute viruses, malware, or other harmful code.\n\n3. Limitation of Liability\n- Tai Nguyen is not responsible for any loss or damage arising from the use of this website.\n- Tai Nguyen does not guarantee that this website will operate uninterrupted or error-free.\n- Content on this website is for reference only and does not constitute professional advice.\n\n4. Links to Other Websites\n- This website may contain links to third-party websites.\n- Tai Nguyen is not responsible for the content or security of these websites.\n\n5. Changes to Terms\n- Tai Nguyen reserves the right to change these terms at any time without prior notice.\n- Your continued use of this website after terms are changed implies acceptance of those changes.\n\n6. Applicable Law\n- These terms are governed by the laws of Vietnam.\n\n7. Contact\n- If you have any questions about these terms, please contact Tai Nguyen via email: [ntainguyen.it@gmail.com]`,
    },
    vi: {
      back: "Quay lại trang web",
      terms: "Điều khoản sử dụng",
      termsContent: `1. Nội dung trang web\n- Tất cả nội dung trên trang web này, bao gồm văn bản, hình ảnh, video và các tài liệu khác, đều thuộc sở hữu của Tai Nguyen hoặc được sử dụng với sự cho phép.\n- Bạn có thể xem và chia sẻ nội dung trên trang web này cho mục đích cá nhân và phi thương mại.\n- Bạn không được phép sao chép, phân phối, sửa đổi hoặc sử dụng nội dung trên trang web này cho bất kỳ mục đích thương mại nào mà không có sự cho phép bằng văn bản của Tai Nguyen.\n\n2. Hành vi người dùng\n- Bạn đồng ý không sử dụng trang web này cho bất kỳ mục đích bất hợp pháp hoặc gây hại nào.\n- Bạn đồng ý không đăng tải hoặc truyền tải bất kỳ nội dung nào có tính chất xúc phạm, phỉ báng, đe dọa hoặc vi phạm quyền riêng tư của người khác.\n- Bạn đồng ý không sử dụng trang web này để phát phát tán virus, phần mềm độc hại hoặc các mã độc hại khác.\n\n3. Giới hạn trách nhiệm\n- Tai Nguyen không chịu trách nhiệm cho bất kỳ tổn thất hoặc thiệt hại nào phát sinh từ việc sử dụng trang web này.\n- Tai Nguyen không đảm bảo rằng trang web này sẽ luôn hoạt động không bị gián đoạn hoặc không có lỗi.\n- Nội dung trên trang web này chỉ mang tính chất tham khảo và không cấu thành lời khuyên chuyên môn.\n\n4. Liên kết đến các trang web khác\n- Trang web này có thể chứa các liên kết đến các trang web của bên thứ ba.\n- Tai Nguyen không chịu trách nhiệm cho nội dung hoặc tính bảo mật của các trang web này.\n\n5. Thay đổi điều khoản\n- Tai Nguyen có quyền thay đổi các điều khoản này bất kỳ lúc nào mà không cần thông báo trước.\n- Việc bạn tiếp tục sử dụng trang web này sau khi các điều khoản được thay đổi đồng nghĩa với việc bạn chấp nhận các thay đổi đó.\n\n6. Luật áp dụng\n- Các điều khoản này được điều chỉnh bởi luật pháp của Việt Nam.\n\n7. Liên hệ\n- Nếu bạn có bất kỳ câu hỏi nào về các điều khoản này, vui lòng liên hệ với Tai Nguyen qua gmail: [ntainguyen.it@gmail.com]`,
    },
  };
  const t = translations[language as keyof typeof translations];

  const formatContent = (text: string) => {
    return text.split("\n").map((line, index) => {
      if (line.trim() === "")
        return <div key={index} className="h-2 md:h-4"></div>;
      if (/^\d+\.\s/.test(line.trim()))
        return (
          <h3
            key={index}
            className="font-bold text-xl md:text-2xl text-black mt-6 mb-3"
          >
            {line}
          </h3>
        );
      return (
        <p
          key={index}
          className="text-gray-700 text-base md:text-lg mb-2 leading-relaxed"
        >
          {line}
        </p>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-gray-50 text-black font-sans overflow-y-auto w-full h-full">
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-16 md:pt-40 md:pb-20">
        <button
          onClick={onClose}
          className="flex items-center text-gray-500 hover:text-black transition-colors mb-8 md:mb-10 font-medium"
        >
          <ArrowLeft size={20} className="mr-2" /> {t.back}
        </button>
        <h1 className="text-3xl md:text-4xl font-bold mb-8 flex items-center">
          <FileText size={36} className="mr-4 text-black" /> {t.terms}
        </h1>
        <div>{formatContent(t.termsContent)}</div>
      </div>
    </div>
  );
};

export const PrivacyContentModal: React.FC<{
  language: string;
  onClose: () => void;
}> = ({ language, onClose }) => {
  // Prevent background scroll
  React.useEffect(() => {
    const originalBodyStyle = document.body.style.overflow;
    const originalHtmlStyle = document.documentElement.style.overflow;
    
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalBodyStyle;
      document.documentElement.style.overflow = originalHtmlStyle;
    };
  }, []);

  const translations = {
    en: {
      back: "Back to website",
      privacy: "Privacy Policy",
      privacyContent: `1. Information We Collect\n- Information you provide: We may collect your personal information when you contact us via email or contact form, such as name, email address, and any other information you choose to provide.\n- Automatically collected information: We may automatically collect certain information when you visit our website, such as IP address, browser type, operating system, and other usage data.\n\n2. How We Use Your Information\n- We may use your personal information to respond to your requests, provide information or services you request, and improve our website.\n- We may use automatically collected information to analyze trends, manage the website, and collect demographic information.\n\n3. Sharing Your Information\n- We will not share your personal information with third parties unless required by law or necessary to protect our rights.\n\n4. Protecting Your Information\n- We implement reasonable measures to protect your personal information from unauthorized access, use, or disclosure.\n\n5. Links to Other Websites\n- Our website may contain links to third-party websites. We are not responsible for the content or privacy policies of these websites.\n\n6. Changes to Privacy Policy\n- We reserve the right to change this privacy policy at any time without prior notice. Your continued use of this website after changes are posted implies acceptance of those changes.\n\n7. Contact\n- If you have any questions about this privacy policy, please contact us via email: [ntainguyen.it@gmail.com]`,
    },
    vi: {
      back: "Quay lại trang web",
      privacy: "Chính sách bảo mật",
      privacyContent: `1. Thông tin mình thu thập\n- Thông tin bạn cung cấp: Mình có thể thu thập thông tin cá nhân của bạn khi bạn liên hệ với mình qua email hoặc biểu mẫu liên hệ, chẳng hạn như tên, địa chỉ email và bất kỳ thông tin nào khác bạn chọn cung cấp.\n- Thông tin tự động thu thập: Mình có thể tự động thu thập một số thông tin nhất định khi bạn truy cập trang web của mình, chẳng hạn như địa chỉ IP, loại trình duyệt, hệ điều hành và các dữ liệu sử dụng khác.\n\n2. Cách mình sử dụng thông tin của bạn\n- Mình có thể sử dụng thông tin cá nhân của bạn để trả lời các yêu cầu của bạn, cung cấp thông tin hoặc dịch vụ mà bạn yêu cầu và cải thiện trang web của mình.\n- Mình có thể sử dụng thông tin tự động thu thập để phân tích xu hướng, quản lý trang web và thu thập thông tin nhân khẩu học.\n\n3. Chia sẻ thông tin của bạn\n- Mình sẽ không chia sẻ thông tin cá nhân của bạn với các bên thứ ba, trừ khi được yêu cầu bởi luật pháp hoặc khi cần thiết để bảo vệ quyền của mình.\n\n4. Bảo mật thông tin của bạn\n- Mình thực hiện các biện pháp hợp lý để bảo vệ thông tin cá nhân của bạn khỏi bị truy cập, sử dụng hoặc tiết lộ trái phép.\n\n5. Liên kết đến các trang web khác\n- Trang web của mình có thể chứa các liên kết đến các trang web của bên thứ ba. Mình không chịu trách nhiệm về nội dung hoặc chính sách bảo mật của các trang web này.\n\n6. Thay đổi chính sách bảo mật\n- Mình có quyền thay đổi chính sách bảo mật này bất kỳ lúc nào mà không cần thông báo trước. Việc bạn tiếp tục sử dụng trang web này sau khi các thay đổi được đăng tải đồng nghĩa với việc bạn chấp nhận các thay đổi đó.\n\n7. Liên hệ\n- Nếu bạn có bất kỳ câu hỏi nào về chính sách bảo mật này, vui lòng liên hệ với mình qua gmail: [ntainguyen.it@gmail.com]`
    },
  };
  const t = translations[language as keyof typeof translations];

  const formatContent = (text: string) => {
    return text.split("\n").map((line, index) => {
      if (line.trim() === "")
        return <div key={index} className="h-2 md:h-4"></div>;
      if (/^\d+\.\s/.test(line.trim()))
        return (
          <h3
            key={index}
            className="font-bold text-xl md:text-2xl text-black mt-6 mb-3"
          >
            {line}
          </h3>
        );
      return (
        <p
          key={index}
          className="text-gray-700 text-base md:text-lg mb-2 leading-relaxed"
        >
          {line}
        </p>
      );
    });
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-gray-50 text-black font-sans overflow-y-auto w-full h-full">
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-16 md:pt-40 md:pb-20">
        <button
          onClick={onClose}
          className="flex items-center text-gray-500 hover:text-black transition-colors mb-8 md:mb-10 font-medium"
        >
          <ArrowLeft size={20} className="mr-2" /> {t.back}
        </button>
        <h1 className="text-3xl md:text-4xl font-bold mb-8 flex items-center">
          <Shield size={36} className="mr-4 text-black" /> {t.privacy}
        </h1>
        <div>{formatContent(t.privacyContent)}</div>
      </div>
    </div>
  );
};

export default React.memo(Footer);
